import {
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {IMediaTrack} from '../../../../dto/MediaTrack.dto';
import IconAddSong from '../../../../asset/icons/icon_addsong.svg';
import WalkupMusicSelectionTable from '../table/WalkupMusicSelectionTable';
import IconWarning from '../../../../asset/icons/icon_warning.svg';
import {productLevelSelector} from '../../../../store/Slices/companySlice';
import {ConditionalRender} from '../../../../components/UserCan';
import CustomModal from '../../../../components/modal/CustomModal';
import CreateClipMusicSelection from '../../../music/on-demand-mode/create-clip/CreateClipMusicSelection';
import {StationProvider} from '../../../../components/models/stations/StationContext';
import UploadSongDialog from '../dialogs/UploadSongDialog';
import {INewWalkupPlayer} from './WalkupLineupStepper';

export type AllowedPlayerTypeKeys = Extract<
  keyof INewWalkupPlayer,
  'hitter' | 'pitcher' | 'athlete'
>;

const SongMappingCmpt = React.memo(
  ({
    isAthlete,
    players,
    setPlayers,
    isPreviousDataDiabled,
    selectedPlayerIdAndType,
  }: {
    isAthlete: boolean;
    players: INewWalkupPlayer[];
    setPlayers: React.Dispatch<React.SetStateAction<INewWalkupPlayer[]>>;
    isPreviousDataDiabled: boolean;
    selectedPlayerIdAndType: {
      id: number;
      type: AllowedPlayerTypeKeys;
      isSongRequestRejected: boolean;
    } | null;
  }) => {
    const productLevel = useSelector(productLevelSelector);
    const isLevel0 = productLevel.includes('Level 0 (ODO Only)');
    const [playerId, setPlayerId] = useState<number>(0);
    const [isUploadDialog, setIsUploadDialog] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [tabType, setTabType] = useState('');
    const [selectedPlayerType, setSelectedPlayerType] =
      useState<AllowedPlayerTypeKeys>('athlete');

    const onSelect = useCallback(
      (track: IMediaTrack) => {
        setPlayers(prev => {
          if (playerId) {
            return prev.map(o => {
              if (o.id === playerId) {
                return {
                  ...o,
                  [selectedPlayerType]: {
                    ...o[selectedPlayerType],
                    file: null,
                    media_track_id: track.id,
                    track,
                  },
                };
              }
              return o;
            });
          }
          return prev;
        });
      },
      [playerId, selectedPlayerType, setPlayers],
    );

    const onDeSelect = useCallback(
      (_track: IMediaTrack) => {
        setPlayers(prev => {
          if (playerId) {
            return prev.map(o => {
              if (o.id === playerId) {
                delete o.track;
                return {
                  ...o,
                  [selectedPlayerType]: {
                    ...o[selectedPlayerType],
                    file: null,
                    media_track_id: null,
                    track_artist: '',
                    track_name: '',
                  },
                };
              }
              return o;
            });
          }
          return prev;
        });
      },
      [playerId, selectedPlayerType, setPlayers],
    );

    const track = useMemo(() => {
      const track = players.find(o => o.id === playerId)?.[
        selectedPlayerType as AllowedPlayerTypeKeys
      ]?.track;
      if (track) {
        return [track];
      }
      return [];
    }, [playerId, players, selectedPlayerType]);

    const handleSongUpload = useCallback(
      (artist: string, track_name: string, file: File | null) => {
        setPlayers(prev =>
          prev.map(o => {
            if (o.id === playerId) {
              const type = o.type === 'new' ? 'new' : 'change';
              const previousSelectedPlayerType = o[selectedPlayerType];
              delete previousSelectedPlayerType?.track;
              return {
                ...o,
                [selectedPlayerType]: {
                  ...previousSelectedPlayerType,
                  type,
                  file,
                  track_artist: artist,
                  track_name: track_name,
                  media_track_id: null,
                },
              };
            }
            return o;
          }),
        );
        setIsUploadDialog(false);
      },
      [playerId, selectedPlayerType, setPlayers],
    );

    useEffect(() => {
      if (
        selectedPlayerIdAndType &&
        selectedPlayerIdAndType.id &&
        selectedPlayerIdAndType.type
      ) {
        setSelectedPlayerType(selectedPlayerIdAndType.type);
        setPlayerId(selectedPlayerIdAndType.id);
      }
    }, [selectedPlayerIdAndType]);

    if (isUploadDialog && playerId) {
      return (
        <UploadSongDialog
          handleSave={handleSongUpload}
          close={() => setIsUploadDialog(false)}
        />
      );
    }

    const handleCloseViewModal = () => {
      setViewModalOpen(false);
    };

    const handleOpenViewModal = () => {
      setViewModalOpen(true);
    };

    return (
      <Grid container>
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            background: `var(--blue_variant36)`,
          }}>
          <Box
            className='flex-column scrollbar h-full overflow-auto justify-between'
            sx={{maxHeight: 'calc(100% - 80px) !important'}}>
            {isAthlete ? (
              <ListPlayerTypeBased
                selectedPlayerType={selectedPlayerType}
                setSelectedPlayerType={setSelectedPlayerType}
                playerId={playerId}
                setPlayerId={setPlayerId}
                players={players}
                playerType='athlete'
                isPreviousDataDiabled={isPreviousDataDiabled}
              />
            ) : (
              <>
                <ListPlayerTypeBased
                  selectedPlayerType={selectedPlayerType}
                  setSelectedPlayerType={setSelectedPlayerType}
                  playerId={playerId}
                  setPlayerId={setPlayerId}
                  players={players}
                  playerType='hitter'
                  isPreviousDataDiabled={isPreviousDataDiabled}
                />
                <ListPlayerTypeBased
                  selectedPlayerType={selectedPlayerType}
                  setSelectedPlayerType={setSelectedPlayerType}
                  playerId={playerId}
                  setPlayerId={setPlayerId}
                  players={players}
                  playerType='pitcher'
                  isPreviousDataDiabled={isPreviousDataDiabled}
                />
              </>
            )}
            {tabType !== 'clip' ? (
              <ConditionalRender condition={!isLevel0}>
                <Button
                  variant='contained'
                  disabled={!playerId}
                  onClick={() => setIsUploadDialog(true)}
                  className='flex px-3 mx-auto'>
                  <img
                    src={IconAddSong}
                    alt='New Song Request Icon'
                    className='mr-4'
                  />
                  Upload a Song
                </Button>
              </ConditionalRender>
            ) : (
              <ConditionalRender condition={!isLevel0}>
                <Button
                  variant='contained'
                  size='small'
                  disabled={!playerId}
                  className='flex px-3 mx-auto'
                  onClick={handleOpenViewModal}>
                  Create Custom Clip
                </Button>
              </ConditionalRender>
            )}
            <StationProvider>
              <CustomModal
                open={viewModalOpen}
                onClose={handleCloseViewModal}
                title='Create Custom Clip'
                body={
                  <CreateClipMusicSelection
                    clipType={'customClip'}
                    walkupType={'walkupClip'}
                    handleCloseViewModal={handleCloseViewModal}
                  />
                }
                actionButtonText='Close Modal'
                onActionButtonClick={handleCloseViewModal}
                maxwidth='2000'
                customHeight='95vh'
              />
            </StationProvider>
          </Box>
        </Grid>
        <Grid item xs={12} md={9} className='px-9'>
          <WalkupMusicSelectionTable
            selectedTracks={track as IMediaTrack[]}
            onSelect={onSelect}
            onDeselect={onDeSelect}
            setTabType={setTabType}
            manualHeight={600}
          />
        </Grid>
      </Grid>
    );
  },
);

export default SongMappingCmpt;

export const ListPlayerTypeBased = ({
  selectedPlayerType,
  setSelectedPlayerType,
  players,
  playerId,
  setPlayerId,
  playerType,
  isStudentForm = false,
  isPreviousDataDiabled = false,
}: {
  selectedPlayerType: AllowedPlayerTypeKeys;
  setSelectedPlayerType: (val: AllowedPlayerTypeKeys) => void;
  players: INewWalkupPlayer[];
  playerId: number;
  setPlayerId: React.Dispatch<React.SetStateAction<number>>;
  playerType: AllowedPlayerTypeKeys;
  isStudentForm?: boolean;
  isPreviousDataDiabled?: boolean;
}) => {
  const isAthelete = playerType === 'athlete';
  const height = isAthelete ? 'h-500' : 'h-320';
  const handleSelect = useCallback(
    (id: number) => {
      setPlayerId(id);
      setSelectedPlayerType(playerType);
    },
    [playerType, setPlayerId, setSelectedPlayerType],
  );
  const totalPlayers = players.filter(
    player =>
      player.player_type?.some(
        type => type.toLowerCase() === playerType.toLowerCase(),
      ),
  ).length;

  const activePlayers = players.filter(
    player => player[playerType]?.track,
  ).length;
  return (
    <>
      {!isAthelete ? (
        <Typography className='text-center mt-1 font-bold'>
          {`${playerType.toUpperCase()} (${activePlayers} / ${totalPlayers})`}
        </Typography>
      ) : null}
      <List
        className={`p-0 mb-2 list-items-sec ${height} overflow-auto scrollbar`}>
        {players
          .filter(
            val =>
              val.player_type
                ?.map(v => v.toLowerCase())
                .includes(playerType.toLowerCase()),
          )
          .map(o => {
            const isDiabled = isPreviousDataDiabled && o.id > 0;
            return (
              <ListItem
                disablePadding
                key={o.id}
                secondaryAction={
                  isStudentForm &&
                  Boolean(o[playerType]?.walkup_track_id) ? null : !(
                      (o[playerType]?.track?.track_artist &&
                        o[playerType]?.track?.track_name) ||
                      (o[playerType]?.track_artist && o[playerType]?.track_name)
                    ) ? (
                    <img
                      src={IconWarning}
                      alt='New Song Request Icon'
                      className='w-26 h-32 pt-1'
                    />
                  ) : null
                }>
                <ListItemButton
                  onClick={() => handleSelect(o.id)}
                  disabled={
                    (Boolean(o[playerType]?.walkup_track_id) &&
                      isStudentForm) ||
                    (isDiabled && !o[playerType]?.song_request_rejected)
                  }
                  selected={
                    o.id === playerId && selectedPlayerType === playerType
                  }>
                  <ListItemText
                    className='ellipsis'
                    primary={`#${o.player_no} ${o.first_name} ${o.last_name}`}
                    secondary={
                      o[playerType]?.track?.track_artist ||
                      o[playerType]?.track_artist
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
      </List>
    </>
  );
};
